import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Bolt as FlashIcon, Stop as StopIcon, FastForward as FastForwardIcon   } from "@mui/icons-material";
import { useSnackbar } from 'notistack';
import io from 'socket.io-client';
import { formatTimeAgo } from '../../../utils/timeSince';

const SongList = ({ songs, onEdit, onDelete }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeFlashSession, setActiveFlashSession] = useState(null);
  const [loadingFlashSession, setLoadingFlashSession] = useState(false);
  const [pushingQuestion, setPushingQuestion] = useState(null);
  const [forcePushingQuestion, setForcePushingQuestion] = useState(null);
  const [stoppingQuestion, setStoppingQuestion] = useState(false);
  const [songQuestions, setSongQuestions] = useState({});
  const [selectedSong, setSelectedSong] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingSong, setPendingSong] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const socketRef = useRef(null);
  const [completedSongIds, setCompletedSongIds] = useState(() => new Set());
  const lastCurrentSongIdRef = useRef(null);
  const recentlyStoppedRef = useRef(false);
  const [pushedAtMap, setPushedAtMap] = useState({});
  const [showOnlyWithQuestions, setShowOnlyWithQuestions] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [lastQuestionsConfigKey, setLastQuestionsConfigKey] = useState(null);
  const songsKey = useMemo(
    () =>
      songs
        .map((song) => song?.songs_master_id)
        .filter(Boolean)
        .join("|"),
    [songs]
  );

  const activeSessionId = activeFlashSession?.session_id;
  const activeSessionQuizConfig = activeFlashSession?.quiz_config;

  useEffect(() => {
    if (!activeFlashSession) {
      setQuestionsLoading(false);
      setLastQuestionsConfigKey(null);
    }
  }, [activeFlashSession]);


  // Load persisted "completed for session" state on session change
  useEffect(() => {
    try {
      const sessionId = activeFlashSession?.session_id;
      if (!sessionId) {
        setCompletedSongIds(new Set());
        return;
      }
      const raw = localStorage.getItem(`flash_completed_${sessionId}`);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          setCompletedSongIds(new Set(arr));
        }
      } else {
        setCompletedSongIds(new Set());
      }
    } catch {
      setCompletedSongIds(new Set());
    }
  }, [activeFlashSession?.session_id]);

  // Persist "completed for session" state whenever it changes
  useEffect(() => {
    try {
      const sessionId = activeFlashSession?.session_id;
      if (!sessionId) return;
      localStorage.setItem(`flash_completed_${sessionId}`, JSON.stringify(Array.from(completedSongIds)));
    } catch {}
  }, [completedSongIds, activeFlashSession?.session_id]);

  // Load persisted "last pushed" times on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('flash_last_pushed_map');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === 'object') {
          setPushedAtMap(parsed);
        }
      }
    } catch {}
  }, []);

  // Persist "last pushed" times whenever updated
  useEffect(() => {
    try {
      localStorage.setItem('flash_last_pushed_map', JSON.stringify(pushedAtMap));
    } catch {}
  }, [pushedAtMap]);

  // Fetch active flash session
  const fetchActiveFlashSession = useCallback(async () => {
    try {
      setLoadingFlashSession(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/live-quiz/flash/active`);
      
      if (!response.ok) throw new Error('Failed to fetch');
      
      const data = await response.json();
      if (data.sessions && data.sessions.length > 0) {
        setActiveFlashSession(data.sessions[0]);
      } else {
        setActiveFlashSession(null);
      }
    } catch (error) {
      console.error('Error fetching flash session:', error);
    } finally {
      setLoadingFlashSession(false);
    }
  }, []);

  // Fetch questions for a song
  const fetchSongQuestions = useCallback(async (songId, config) => {
    try {
      // console.log(`[SongList] Fetching questions for song ${songId} with config:`, config);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/quiz/song/${songId}?` +
        `difficulty_id=${config?.difficulty_id || 7}&` +
        `questions_per_song=${config?.questions_per_song || 3}`
      );
      
      // console.log(`[SongList] Response status for song ${songId}:`, response.status);
      
      if (!response.ok) {
        console.error(`[SongList] Failed to fetch questions for song ${songId}:`, response.status);
        return null;
      }
      
      const data = await response.json();
      // console.log(`[SongList] Questions data for song ${songId}:`, data);
      return data.questions || [];
    } catch (error) {
      console.error(`[SongList] Error fetching questions for song ${songId}:`, error);
      return null;
    }
  }, []);

  // Load questions for all songs (for Push when session active, for Force always)
  useEffect(() => {
    if (songs.length === 0) return;

    const config =
      activeSessionId && activeSessionQuizConfig
        ? (typeof activeSessionQuizConfig === "string"
            ? (() => {
                try {
                  return JSON.parse(activeSessionQuizConfig);
                } catch {
                  return {};
                }
              })()
            : activeSessionQuizConfig)
        : { difficulty_id: 7, questions_per_song: 1 };

    // Include song ids in the config key to refresh when list changes
    const configKey = `${
      activeSessionId || "no-session"
    }-${config?.difficulty_id || 7}-${config?.questions_per_song || 1}-${songsKey}`;

    if (lastQuestionsConfigKey === configKey) return;

    let isActive = true;
    const loadQuestions = async () => {
      setQuestionsLoading(true);
      // console.log(
      //   `[SongList] Loading questions for ${songs.length} songs with config:`,
      //   config
      // );
      try {
        // Fetch all questions in parallel instead of sequentially
        const questionPromises = songs.map((song) =>
          fetchSongQuestions(song.songs_master_id, config).then((questions) => ({
            songId: song.songs_master_id,
            questions
          }))
        );
        
        const results = await Promise.all(questionPromises);
        
        if (isActive) {
          const questionsMap = {};
          results.forEach(({ songId, questions }) => {
            questionsMap[songId] = questions;
          });
          setSongQuestions(questionsMap);
          setLastQuestionsConfigKey(configKey);
        }
      } catch (error) {
        console.error("[SongList] Failed to load questions:", error);
      } finally {
        if (isActive) {
          setQuestionsLoading(false);
        }
      }
    };

    loadQuestions();

    return () => {
      isActive = false;
    };
  }, [
    activeSessionId,
    activeSessionQuizConfig,
    songs,
    songsKey,
    fetchSongQuestions,
    lastQuestionsConfigKey,
  ]);

  // Fetch active session on mount only
  useEffect(() => {
    fetchActiveFlashSession();
  }, [fetchActiveFlashSession]);

  // WebSocket connection for real-time session updates
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const socket = io(baseUrl, { transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      // console.log('[SongList] Socket connected');
      socket.emit('flash:join-global');
    });

    // Listen for question pushed - update session state
    socket.on('flash:question_pushed', async (payload) => {
      if (payload && payload.session_id) {
        // console.log('[SongList] Question pushed event received:', payload.session_id);
        // Fetch updated session to get latest rapid_config
        await fetchActiveFlashSession();
        // Update last pushed map using payload if available
        try {
          if (payload.current_song?.songs_master_id && payload.pushed_at) {
            setPushedAtMap(old => ({
              ...old,
              [payload.current_song.songs_master_id]: payload.pushed_at
            }));
          }
        } catch {}
      }
    });

    // Listen for question stopped - update session state
    socket.on('flash:question_stopped', async (payload) => {
      if (payload && payload.session_id) {
        // console.log('[SongList] Question stopped event received:', payload.session_id);
        // Mark that the last end was an explicit stop, not a completed timer
        recentlyStoppedRef.current = true;
        // Fetch updated session to get latest rapid_config
        await fetchActiveFlashSession();
      }
    });

    // Listen for session ended
    socket.on('flash:session_ended', async (payload) => {
      if (payload && payload.session_id) {
        // console.log('[SongList] Session ended event received:', payload.session_id);
        // Clear active session if it's the one that ended
        setActiveFlashSession(prev => {
          if (prev && prev.session_id === payload.session_id) {
            return null;
          }
          return prev;
        });
        // Clear completed state for ended session
        try {
          localStorage.removeItem(`flash_completed_${payload.session_id}`);
        } catch {}
        setCompletedSongIds(new Set());
      }
    });

    // Listen for session activated
    socket.on('flash:activated', async (payload) => {
      if (payload && payload.session_id) {
        // console.log('[SongList] Session activated event received:', payload.session_id);
        // Fetch updated session list
        await fetchActiveFlashSession();
      }
    });

    return () => {
      if (socketRef.current) {
        // console.log('[SongList] Cleaning up socket connection');
        socketRef.current.emit('flash:leave-global');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [fetchActiveFlashSession]);

  // Update current time every second for timer display
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Track when a song's timer completes to disable re-push for that song
  // Note: this effect is declared AFTER getTimerState is defined to avoid TDZ issues

  // Get current timer state
  const getTimerState = useCallback(() => {
    if (!activeFlashSession?.rapid_config) return null;
    
    try {
      const rapidConfig = typeof activeFlashSession.rapid_config === 'string' 
        ? JSON.parse(activeFlashSession.rapid_config) 
        : activeFlashSession.rapid_config;
      
      const pushedAt = rapidConfig.question_pushed_at;
      const deliveryTime = rapidConfig.question_delivery_time;
      const songTimerSeconds = rapidConfig.song_timer_seconds || 60;
      const currentSongId = rapidConfig.current_song_id;
      
      if (!pushedAt || !deliveryTime || !currentSongId) return null;
      
      const now = currentTime;
      const delivery = new Date(deliveryTime).getTime();
      const quizletEnd = delivery + (songTimerSeconds * 1000);
      
      // Check if we're in delay phase
      if (now < delivery) {
        const remaining = Math.max(0, Math.ceil((delivery - now) / 1000));
        return {
          phase: 'delay',
          remainingSeconds: remaining,
          currentSongId,
          songTimerSeconds
        };
      }
      
      // Check if we're in quizlet timer phase
      if (now < quizletEnd) {
        const remaining = Math.max(0, Math.ceil((quizletEnd - now) / 1000));
        return {
          phase: 'quizlet',
          remainingSeconds: remaining,
          currentSongId,
          songTimerSeconds
        };
      }
      
      // Timer ended
      return null;
    } catch {
      return null;
    }
  }, [activeFlashSession, currentTime]);

  // Format timer display
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Load last pushed timestamps from server for current list
  useEffect(() => {
    const fetchMap = async () => {
      try {
        if (!Array.isArray(songs) || songs.length === 0) return;
        const ids = songs.map(s => s?.songs_master_id).filter(Boolean);
        if (ids.length === 0) return;
        const base = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const resp = await fetch(`${base}/api/songs/last-pushed/map?ids=${ids.join(',')}`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (data?.map) {
          setPushedAtMap(prev => ({ ...prev, ...data.map }));
        }
      } catch (e) {
        console.warn('[SongList] last-pushed fetch failed:', e?.message);
      }
    };
    fetchMap();
  }, [songs]);

  // Track when a song's timer completes to disable re-push for that song
  useEffect(() => {
    const timerState = getTimerState();
    const previousSongId = lastCurrentSongIdRef.current;
    if (timerState && timerState.currentSongId) {
      lastCurrentSongIdRef.current = timerState.currentSongId;
    } else {
      if (previousSongId && !recentlyStoppedRef.current) {
        setCompletedSongIds(prev => {
          const updated = new Set(prev);
          updated.add(previousSongId);
          return updated;
        });
      }
      recentlyStoppedRef.current = false;
      lastCurrentSongIdRef.current = null;
    }
  }, [getTimerState]);

  // Get current running song name
  const getCurrentRunningSongName = useCallback(() => {
    const timerState = getTimerState();
    if (!timerState) return null;
    
    // Find song from songs list
    const song = songs.find(s => s.songs_master_id === timerState.currentSongId);
    return song ? song.name : 'Another song';
  }, [getTimerState, songs]);

  const handlePushClick = (song) => {
    const timerState = getTimerState();
    
    // Check if another song is currently running
    if (timerState && timerState.currentSongId !== song.songs_master_id) {
      setPendingSong(song);
      setConfirmDialogOpen(true);
      return;
    }
    
    handlePushConfirm(song);
  };

  const handlePushConfirm = async (songParam = null) => {
    const songToPush = songParam || pendingSong || selectedSong;
    if (!songToPush || !activeFlashSession) return;

    try {
      setPushingQuestion(songToPush.songs_master_id);

      // Use delay from session rapid_config
      const rawRapid = activeFlashSession?.rapid_config;
      const parsedRapid = typeof rawRapid === 'string' ? (() => { try { return JSON.parse(rawRapid); } catch { return {}; } })() : (rawRapid || {});
      const sessionDelay = typeof parsedRapid.delay_seconds === 'number' ? parsedRapid.delay_seconds : 10;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/flash/sessions/${activeFlashSession.session_id}/push-song/${songToPush.songs_master_id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          },
          body: JSON.stringify({
            delay_seconds: sessionDelay
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to push question');
      }

      enqueueSnackbar(`Question pushed! Delay per config: ${sessionDelay}s`, { variant: 'success' });
      // Record this song as pushed with current timestamp (server will also emit)
      setPushedAtMap(prev => ({ ...prev, [songToPush.songs_master_id]: new Date().toISOString() }));
      // Session will be updated via WebSocket event
    } catch (error) {
      console.error('Error pushing question:', error);
      enqueueSnackbar(error.message || 'Failed to push question', { variant: 'error' });
    } finally {
      setPushingQuestion(null);
      setSelectedSong(null);
      setPendingSong(null);
      setConfirmDialogOpen(false);
    }
  };

  // Stop current question
  const handleStopQuestion = async () => {
    if (!activeFlashSession) {
      enqueueSnackbar('No active flash quiz session', { variant: 'warning' });
      return;
    }

    try {
      // Mark that next end should not count as completed
      recentlyStoppedRef.current = true;
      setStoppingQuestion(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/live-quiz/flash/sessions/${activeFlashSession.session_id}/stop-question`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to stop question');
      }

      enqueueSnackbar('Question stopped successfully', { variant: 'success' });
      // Session will be updated via WebSocket event
    } catch (error) {
      console.error('Error stopping question:', error);
      enqueueSnackbar(error.message || 'Failed to stop question', { variant: 'error' });
    } finally {
      setStoppingQuestion(false);
    }
  };

  const hasQuestions = (songId) => {
    const questions = songQuestions[songId];
    return questions && questions.length > 0;
  };

  const handleForceClick = async (song) => {
    if (!hasQuestions(song.songs_master_id)) {
      enqueueSnackbar('No questions available for this song', { variant: 'warning' });
      return;
    }
    try {
      setForcePushingQuestion(song.songs_master_id);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/quiz/force-push/${song.songs_master_id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
          },
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to force push question');
      }
      enqueueSnackbar(data.message || 'Question pushed to logged-in users', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.message || 'Failed to force push', { variant: 'error' });
    } finally {
      setForcePushingQuestion(null);
    }
  };

  const songsToRender = useMemo(() => {
    if (!showOnlyWithQuestions || !activeFlashSession) return songs;
    return songs.filter((song) => {
      const questions = songQuestions[song.songs_master_id];
      return questions && questions.length > 0;
    });
  }, [songs, showOnlyWithQuestions, activeFlashSession, songQuestions]);

  // Removed session-scoped last push helper; using persisted per-song map now

  // Auto-close confirmation dialog when timer ends
  useEffect(() => {
    const timerState = getTimerState();
    if (!timerState && confirmDialogOpen) {
      setConfirmDialogOpen(false);
      setPendingSong(null);
    }
  }, [getTimerState, confirmDialogOpen]);

  // Get button state for a song
  const getButtonState = (song) => {
    const timerState = getTimerState();
    const isCurrentSong = timerState && timerState.currentSongId === song.songs_master_id;
    const isCompleted = completedSongIds.has(song.songs_master_id);
    
    if (!timerState) {
      return { type: 'push', disabled: isCompleted };
    }
    
    if (isCurrentSong) {
      return {
        type: 'stop',
        color: timerState.phase === 'delay' ? 'success' : 'error', // green for delay, red for quizlet
        disabled: stoppingQuestion,
        timer: formatTimer(timerState.remainingSeconds),
        phase: timerState.phase
      };
    }
    
    return { type: 'push', disabled: isCompleted };
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 1,
        }}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showOnlyWithQuestions}
              onChange={(event) =>
                setShowOnlyWithQuestions(event.target.checked)
              }
              disabled={!activeFlashSession}
            />
          }
          label="Only show songs with flash questions"
          sx={{
            ml: -1,
            color: activeFlashSession ? "#fff" : "#888",
            userSelect: "none",
          }}
        />
        {activeFlashSession && (
          <Typography variant="caption" sx={{ color: "#999" }}>
            {questionsLoading
              ? "Checking question availability..."
              : `${songsToRender.length} of ${songs.length} songs ready`}
          </Typography>
        )}
      </Box>

      {songsToRender.length === 0 ? (
        <Typography variant="body2" sx={{ mt: 3, color: "#999" }}>
          {!activeFlashSession
            ? "Activate a flash quiz session to load song questions."
            : showOnlyWithQuestions
            ? "No songs have questions for the current flash quiz config."
            : "No songs available."}
        </Typography>
      ) : (
        songsToRender.map((song) => {
          const buttonState = getButtonState(song);
          const hasQ = hasQuestions(song.songs_master_id);
          
          return (
          <Box
            key={song.songs_master_id}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <img
                src={song.image || "https://placehold.co/50x50"}
                alt="cover"
                style={{ width: 50, height: 50, borderRadius: 8 }}
              />
              <Box>
                <Typography variant="h6">{song.name}</Typography>
                <Typography variant="subtitle2">{song.artist}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Last Pushed (always show if this song was pushed before, regardless of session) */}
              {pushedAtMap[song.songs_master_id] && (
                <Typography variant="caption" sx={{ color: '#999', mr: 1 }}>
                  Last pushed: {new Date(pushedAtMap[song.songs_master_id]).toLocaleString()} ({formatTimeAgo(pushedAtMap[song.songs_master_id])})
                </Typography>
              )}
              {/* Flash Quiz Button */}
              {loadingFlashSession ? (
                <Button
                  variant="outlined"
                  size="small"
                  disabled
                  sx={{
                    borderColor: '#666',
                    color: '#666',
                    mr: 1
                  }}
                >
                  Loading...
                </Button>
              ) : activeFlashSession ? (
                <>
                  {/* Timer Display */}
                  {buttonState.type === 'stop' && buttonState.timer && (
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: buttonState.phase === 'delay' ? '#4caf50' : '#f44336',
                        fontWeight: 'bold',
                        minWidth: '60px',
                        mr: 1
                      }}
                    >
                      {buttonState.timer}
                    </Typography>
                  )}
                  
                  {/* Single Push/Stop Button */}
                  {hasQ ? (
                    <Button
                      variant={buttonState.type === 'push' ? 'contained' : 'outlined'}
                      size="small"
                      startIcon={
                        buttonState.type === 'push' 
                          ? (pushingQuestion === song.songs_master_id ? <CircularProgress size={16} /> : <FlashIcon />)
                          : (stoppingQuestion ? <CircularProgress size={16} /> : <StopIcon />)
                      }
                      disabled={buttonState.disabled || (buttonState.type === 'push' && pushingQuestion === song.songs_master_id)}
                      onClick={() => {
                        if (buttonState.type === 'push') {
                          handlePushClick(song);
                        } else {
                          handleStopQuestion();
                        }
                      }}
                      sx={{
                        ...(buttonState.type === 'push' ? {
                          bgcolor: '#9C27B0',
                          '&:hover': { bgcolor: '#7B1FA2' },
                        } : {
                          borderColor: buttonState.color === 'success' ? '#4caf50' : '#f44336',
                          color: buttonState.color === 'success' ? '#4caf50' : '#f44336',
                          '&:hover': { 
                            borderColor: buttonState.color === 'success' ? '#45a049' : '#d32f2f',
                            bgcolor: buttonState.color === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)'
                          },
                        }),
                        mr: 1
                      }}
                    >
                      {buttonState.type === 'push' 
                        ? (pushingQuestion === song.songs_master_id ? 'Pushing...' : 'Push')
                        : 'Stop'
                      }
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled
                      sx={{
                        borderColor: '#666',
                        color: '#666',
                        mr: 1
                      }}
                    >
                      No Questions
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  disabled
                  sx={{
                    borderColor: '#f44336',
                    color: '#f44336',
                    mr: 1
                  }}
                >
                  Session Not Active
                </Button>
              )}

              {/* Force: single question to logged-in users (no session required) */}
              {hasQ && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    forcePushingQuestion === song.songs_master_id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <FastForwardIcon />
                    )
                  }
                  disabled={forcePushingQuestion === song.songs_master_id}
                  onClick={() => handleForceClick(song)}
                  sx={{
                    borderColor: '#ff9800',
                    color: '#ff9800',
                    mr: 1,
                    '&:hover': {
                      borderColor: '#f57c00',
                      bgcolor: 'rgba(255, 152, 0, 0.1)',
                    },
                  }}
                >
                  {forcePushingQuestion === song.songs_master_id ? 'Pushing...' : 'Force'}
                </Button>
              )}
              
              <IconButton onClick={() => onEdit(song.songs_master_id)}>
                <EditIcon />
              </IconButton>
              <IconButton onClick={() => onDelete(song.songs_master_id)}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
          );
        })
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false);
          setPendingSong(null);
        }}
        PaperProps={{
          sx: {
            bgcolor: '#1e1e1e',
            color: 'white',
            borderRadius: '12px',
            minWidth: '500px',
            maxWidth: '600px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Confirm Push Question
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', mb: 2 }}>
            {/* Song Card */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  p: 2,
                  bgcolor: '#2a2a2a',
                  borderRadius: '8px',
                  border: '1px solid #333'
                }}
              >
                <img
                  src={pendingSong?.image || "https://placehold.co/80x80"}
                  alt={pendingSong?.name || 'Song'}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 8,
                    objectFit: 'cover'
                  }}
                />
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#fff' }}>
                    {pendingSong?.name || 'Unknown Song'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    {pendingSong?.artist || 'Unknown Artist'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Timer Display */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '120px' }}>
              {(() => {
                const timerState = getTimerState();
                if (!timerState) return null;
                
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      p: 2,
                      bgcolor: timerState.phase === 'delay' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                      borderRadius: '8px',
                      border: `1px solid ${timerState.phase === 'delay' ? '#4caf50' : '#f44336'}`,
                      minWidth: '100px'
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#999', mb: 0.5 }}>
                      {timerState.phase === 'delay' ? 'Delay' : 'Quiz Timer'}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: timerState.phase === 'delay' ? '#4caf50' : '#f44336',
                        fontFamily: 'monospace'
                      }}
                    >
                      {formatTimer(timerState.remainingSeconds)}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
          </Box>

          <Typography variant="body2" sx={{ color: '#999', mt: 2 }}>
            {(() => {
              const runningSongName = getCurrentRunningSongName();
              const currentSongName = pendingSong?.name || 'this song';
              return `${runningSongName} is currently running. Do you want to stop it and push ${currentSongName}?`;
            })()}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false);
              setPendingSong(null);
            }}
            variant="outlined"
            sx={{
              color: '#fff',
              borderColor: '#666',
              '&:hover': {
                borderColor: '#888',
                bgcolor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handlePushConfirm()}
            variant="contained"
            disabled={pushingQuestion === pendingSong?.songs_master_id}
            startIcon={pushingQuestion === pendingSong?.songs_master_id ? <CircularProgress size={16} /> : null}
            sx={{
              bgcolor: '#9C27B0',
              '&:hover': { bgcolor: '#7B1FA2' },
              minWidth: '100px'
            }}
          >
            {pushingQuestion === pendingSong?.songs_master_id ? 'Pushing...' : 'Push'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SongList;
